import React, { useRef, useEffect, useState, memo } from 'react';
import { Link } from 'react-router-dom';
import Transition from '../utils/Transition';
import { useUser } from '../userContext';

const SearchModal = memo(({ id, searchId, modalOpen, setModalOpen }) => {
  const { campaigns } = useUser();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const modalContent = useRef(null);
  const searchInput = useRef(null);

  // Updated search functionality
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }


    const term = searchTerm.toLowerCase().trim();
    const filtered = campaigns?.filter(campaign => {
      const title = (campaign.name || '').toLowerCase();
      const description = (campaign.description || '').toLowerCase();
      const gameType = (campaign.gameType || '').toLowerCase();
      
      return title.includes(term) || 
             description.includes(term) || 
             gameType.includes(term);
    });

    setSearchResults(filtered || []);
  }, [searchTerm, campaigns]);

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!modalOpen || modalContent.current.contains(target)) return
      setModalOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!modalOpen || keyCode !== 27) return;
      setModalOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  useEffect(() => {
    modalOpen && searchInput.current.focus();
  }, [modalOpen]);

  const getImage = (campaign) => {
    // Try to get images in order of preference
    const image = campaign.options?.sharingPhoto || 
                  campaign.options?.coverPhoto || 
                  '/twitter.png'; // Fallback to logo

    return (
      <img 
        src={image}
        alt={campaign.name}
        className="w-8 h-8 rounded object-cover mr-3"
        style={{
          border: '1px solid rgba(241, 196, 15, 0.3)'
        }}
      />
    );
  };

  return (
    <>
      {/* Modal backdrop */}
      <Transition
        className="fixed inset-0 bg-black bg-opacity-70 z-30 transition-opacity"
        style={{
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)'
        }}
        show={modalOpen}
        enter="transition ease-out duration-200"
        enterStart="opacity-0"
        enterEnd="opacity-100"
        leave="transition ease-out duration-100"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
        aria-hidden="true"
      />
      {/* Modal dialog */}
      <Transition
        id={id}
        className="fixed inset-0 z-50 flex justify-center px-4 sm:px-6 pt-20"
        style={{
          alignItems: 'flex-start'
        }}
        role="dialog"
        aria-modal="true"
        show={modalOpen}
        enter="transition ease-in-out duration-200"
        enterStart="opacity-0 translate-y-4"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-in-out duration-200"
        leaveStart="opacity-100 translate-y-0"
        leaveEnd="opacity-0 translate-y-4"
      >
        <div
          ref={modalContent}
          className="overflow-auto max-w-2xl w-full rounded-lg"
          style={{
            fontFamily: '"Share Tech Mono", monospace',
            background: 'linear-gradient(135deg, #2C3E50, #34495E)',
            border: '2px solid rgba(241, 196, 15, 0.6)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.7)',
            maxHeight: 'calc(100vh - 20rem)'
          }}
        >
          {/* Search form */}
          <form className="border-b-2" style={{
            borderColor: 'rgba(241, 196, 15, 0.3)'
          }} onSubmit={(e) => e.preventDefault()}>
            <div className="relative">
              <label htmlFor={searchId} className="sr-only">🔍 SEARCH CAMPAIGNS</label>
              <input
                id={searchId}
                className="w-full appearance-none py-4 pl-12 pr-4 text-base font-medium"
                style={{
                  background: 'rgba(52, 73, 94, 0.4)',
                  border: 'none',
                  color: '#F1C40F',
                  fontFamily: '"Share Tech Mono", monospace'
                }}
                type="search"
                placeholder="SEARCH CAMPAIGNS..."
                ref={searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button className="absolute inset-0 right-auto group flex items-center" type="submit" aria-label="Search">
                <span className="ml-4 mr-2 text-xl">🔍</span>
              </button>
            </div>
          </form>

          <div className="py-3 px-2">
            {/* Search Results */}
            {searchTerm && (
              <div className="mb-3 last:mb-0">
                <div className="text-xs font-bold uppercase px-2 mb-2 tracking-wide"
                  style={{
                    color: '#F1C40F',
                    textShadow: '0 0 8px rgba(241, 196, 15, 0.5)',
                    fontFamily: '"Share Tech Mono", monospace'
                  }}
                >
                  🎯 SEARCH RESULTS
                </div>
                <ul className="text-sm">
                  {searchResults.length > 0 ? (
                    searchResults.map(campaign => (
                      <li key={campaign.id}>
                        <Link
                          className="flex items-center p-3 rounded-lg transition-all duration-100 font-medium"
                          style={{
                            color: '#ECF0F1',
                            fontFamily: '"Share Tech Mono", monospace'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'rgba(241, 196, 15, 0.1)';
                            e.target.style.border = '1px solid rgba(241, 196, 15, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'transparent';
                            e.target.style.border = '1px solid transparent';
                          }}
                          to={`/studio/campaign/${campaign.id}`}
                          onClick={() => setModalOpen(false)}
                        >
                          {getImage(campaign)}
                          <div className="flex flex-col">
                            <span className="font-bold" style={{ color: '#F1C40F' }}>{campaign.name}</span>
                            {campaign.options?.title && (
                              <span className="text-sm" style={{ color: '#BDC3C7' }}>{campaign.options.title}</span>
                            )}
                          </div>
                        </Link>
                      </li>
                    ))
                  ) : (
                    <li className="px-3 py-2 font-medium" style={{ 
                      color: '#95A5A6',
                      fontFamily: '"Share Tech Mono", monospace'
                    }}>❌ NO CAMPAIGNS FOUND</li>
                  )}
                </ul>
              </div>
            )}

            {/* Recent Campaigns */}
            {!searchTerm && campaigns && campaigns.length > 0 && (
              <div className="mb-3 last:mb-0">
                <div className="text-xs font-bold uppercase px-2 mb-2 tracking-wide"
                  style={{
                    color: '#F1C40F',
                    textShadow: '0 0 8px rgba(241, 196, 15, 0.5)',
                    fontFamily: '"Share Tech Mono", monospace'
                  }}
                >
                  ⚡ RECENT CAMPAIGNS
                </div>
                <ul className="text-sm">
                  {campaigns
                    .sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds)
                    .slice(0, 3)
                    .map(campaign => (
                      <li key={campaign.id}>
                        <Link
                          className="flex items-center p-3 rounded-lg transition-all duration-100 font-medium"
                          style={{
                            color: '#ECF0F1',
                            fontFamily: '"Share Tech Mono", monospace'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'rgba(241, 196, 15, 0.1)';
                            e.target.style.border = '1px solid rgba(241, 196, 15, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'transparent';
                            e.target.style.border = '1px solid transparent';
                          }}
                          to={`/studio/campaign/${campaign.id}`}
                          onClick={() => setModalOpen(false)}
                        >
                          {getImage(campaign)}
                          <div className="flex flex-col">
                            <span className="font-bold" style={{ color: '#F1C40F' }}>{campaign.name}</span>
                            {campaign.options?.title && (
                              <span className="text-sm" style={{ color: '#BDC3C7' }}>{campaign.options.title}</span>
                            )}
                          </div>
                        </Link>
                      </li>
                    ))}
                </ul>
              </div>
            )}

            {/* Empty state */}
            {!searchTerm && (!campaigns || campaigns.length === 0) && (
              <div className="text-center py-8">
                <div className="text-4xl mb-2">🎮</div>
                <div className="font-bold mb-2" style={{ 
                  color: '#F1C40F',
                  fontFamily: '"Share Tech Mono", monospace'
                }}>NO CAMPAIGNS YET</div>
                <div className="text-sm" style={{ 
                  color: '#95A5A6',
                  fontFamily: '"Share Tech Mono", monospace'
                }}>CREATE YOUR FIRST CAMPAIGN TO GET STARTED</div>
              </div>
            )}
          </div>
        </div>
      </Transition>
    </>
  );
});

export default SearchModal;
